import { useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  Card,
  Empty,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { EditOutlined, ExperimentOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import dayjs from "dayjs";

import { apiClient } from "../../services/api/apiClient";
import { usePageTitle } from "../../hooks";
import { PageHeader } from "../../components/common/PageHeader";

// Same regex as the backend (AssignLabCodeSerializer.LAB_CODE_RE) —
// 5 chars, A-Z/0-9, first char can't be 'O'. Duplicated on purpose so
// the UI can fail fast without a round-trip.
const LAB_CODE_RE = /^[A-NP-Z0-9][A-Z0-9]{4}$/;

interface SampleRow {
  id: string;
  sample_reference: string;
  sample_type: "leaf" | "skin";
  lab_code: string | null;
  is_retention: boolean;
  audit: number;
  audit_reference: string;
  producer_name: string | null;
  farm_name: string | null;
  audit_start_date: string | null;
  audit_plot: number | null;
  audit_plot_name: string | null;
  // Etiqueta legible del planting (parcela doblada). Null cuando la
  // parcela tiene un único planting o cuando la muestra es histórica.
  audit_plot_planting_label: string | null;
  audit_plot_planting_ordering: number | null;
  control_point_review: number | null;
  control_point_review_name: string | null;
  created: string;
}

interface PagedResponse<T> {
  data: T[];
  total: number;
}

type Scope = "pending" | "retention" | "processed" | "all";

const FILTER_OPTIONS: Array<{ label: string; value: Scope }> = [
  // "Pendientes": sin código Y sin marcar retén — lo que aún requiere
  // una decisión del admin tras el push desde campo.
  { label: "Pendientes de decidir", value: "pending" },
  { label: "Retén", value: "retention" },
  { label: "Procesadas (con código)", value: "processed" },
  { label: "Todas", value: "all" },
];

export const AuditorSamplesPending = () => {
  const { t } = useTranslation();
  const { message: appMessage } = App.useApp();
  usePageTitle(
    t("samples.pending.title", "Muestras pendientes de código de laboratorio"),
  );

  const [rows, setRows] = useState<SampleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [scope, setScope] = useState<Scope>("pending");

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<string>("");
  const [saving, setSaving] = useState(false);
  // ID de la fila sobre la que se está aplicando una acción (marcar
  // retén, desmarcar). Permite deshabilitar sólo ese botón en vez
  // del `saving` global que afecta al editor de lab_code.
  const [actingOnId, setActingOnId] = useState<string | null>(null);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page_size: "200" });
      // Mapeo del scope UI → filtros del backend. "Pendientes" exige
      // sin lab_code Y sin retén, los otros scopes son una sola
      // condición. `all` no manda ningún filtro.
      if (scope === "pending") {
        params.set("has_lab_code", "false");
        params.set("is_retention", "false");
      } else if (scope === "retention") {
        params.set("is_retention", "true");
      } else if (scope === "processed") {
        params.set("has_lab_code", "true");
      }
      const res = await apiClient.get<PagedResponse<SampleRow>>(
        `/v1/samples/?${params.toString()}`,
      );
      // The endpoint uses `RefineCompatiblePagination` — `res.data` is
      // shaped `{ data, total }` for list actions.
      const list =
        (res.data as any)?.data ??
        (res.data as any)?.results ??
        (Array.isArray(res.data) ? res.data : []);
      setRows(list as SampleRow[]);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      return (
        (r.audit_reference || "").toLowerCase().includes(q) ||
        (r.audit_plot_name || "").toLowerCase().includes(q) ||
        (r.control_point_review_name || "").toLowerCase().includes(q) ||
        (r.sample_reference || "").toLowerCase().includes(q) ||
        (r.lab_code || "").toLowerCase().includes(q) ||
        (r.producer_name || "").toLowerCase().includes(q) ||
        (r.farm_name || "").toLowerCase().includes(q)
      );
    });
  }, [rows, searchText]);

  const handleSetRetention = async (record: SampleRow, next: boolean) => {
    setActingOnId(record.id);
    try {
      await apiClient.patch(`/v1/samples/${record.id}/set_retention/`, {
        is_retention: next,
      });
      appMessage.success(
        next
          ? t("sample.retention.marked", "Muestra marcada como retén.")
          : t("sample.retention.cleared", "Marca de retén quitada."),
      );
      await fetchRows();
    } catch (err: any) {
      const resp = err?.originalError?.response?.data;
      const detail =
        (resp && (resp.is_retention || resp.detail)) ||
        err?.message ||
        t("sample.retention.error", "No se pudo actualizar la muestra.");
      const text = Array.isArray(detail) ? detail.join(" ") : String(detail);
      appMessage.error(text);
    } finally {
      setActingOnId(null);
    }
  };

  const handleAssign = async (record: SampleRow) => {
    const value = draft.trim().toUpperCase();
    if (!value) {
      appMessage.error(
        t("sample.lab_code.empty", "Introduce un código de laboratorio."),
      );
      return;
    }
    if (!LAB_CODE_RE.test(value)) {
      appMessage.error(
        t(
          "sample.lab_code.invalid_format",
          "El código debe tener 5 caracteres (letras mayúsculas o dígitos) y no puede empezar por «O».",
        ),
      );
      return;
    }
    setSaving(true);
    try {
      await apiClient.patch(`/v1/samples/${record.id}/assign_lab_code/`, {
        lab_code: value,
      });
      appMessage.success(
        t("sample.lab_code.saved", "Código de laboratorio guardado."),
      );
      setEditingId(null);
      setDraft("");
      // In pending scope the assigned row drops from the list; otherwise
      // just refresh the value in place.
      await fetchRows();
    } catch (err: any) {
      const resp = err?.originalError?.response?.data;
      const detail =
        (resp && (resp.lab_code || resp.detail)) ||
        err?.message ||
        t("sample.lab_code.error", "No se pudo guardar el código.");
      const text = Array.isArray(detail) ? detail.join(" ") : String(detail);
      appMessage.error(text);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: t("sample.status.column", "Estado"),
      key: "lab_code",
      width: 260,
      render: (_: unknown, record: SampleRow) => {
        if (editingId === record.id) {
          return (
            <Space size={4}>
              <Input
                size="small"
                autoFocus
                maxLength={5}
                value={draft}
                onChange={(e) => setDraft(e.target.value.toUpperCase())}
                onPressEnter={() => handleAssign(record)}
                placeholder="AB123"
                style={{ width: 90, textTransform: "uppercase" }}
              />
              <Button
                size="small"
                type="primary"
                loading={saving}
                onClick={() => handleAssign(record)}
              >
                {t("common.save", "Guardar")}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setEditingId(null);
                  setDraft("");
                }}
              >
                {t("common.cancel", "Cancelar")}
              </Button>
            </Space>
          );
        }
        if (record.is_retention) {
          // Retén: badge visible + botón para deshacer la marca por si
          // el admin se equivoca o cambia de opinión. Al desmarcar la
          // muestra vuelve al scope "Pendientes de decidir".
          return (
            <Space size={4}>
              <Tag color="default">
                {t("sample.retention.badge", "Retén")}
              </Tag>
              <Button
                size="small"
                type="link"
                loading={actingOnId === record.id}
                onClick={() => handleSetRetention(record, false)}
              >
                {t("sample.retention.unmark", "Quitar retén")}
              </Button>
            </Space>
          );
        }
        if (!record.lab_code) {
          return (
            <Space size={4}>
              <Button
                size="small"
                type="primary"
                icon={<EditOutlined />}
                onClick={() => {
                  setEditingId(record.id);
                  setDraft("");
                }}
              >
                {t("sample.lab_code.assign", "Asignar código")}
              </Button>
              <Button
                size="small"
                loading={actingOnId === record.id}
                onClick={() => handleSetRetention(record, true)}
              >
                {t("sample.retention.mark", "Marcar retén")}
              </Button>
            </Space>
          );
        }
        return (
          <Space size={4}>
            <strong>{record.lab_code}</strong>
            <Button
              size="small"
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingId(record.id);
                setDraft(record.lab_code ?? "");
              }}
              title={t("common.edit", "Editar")}
            />
          </Space>
        );
      },
    },
    {
      title: t("sample.reference", "Referencia app"),
      dataIndex: "sample_reference",
      key: "sample_reference",
      width: 160,
      render: (v: string) => (
        <Typography.Text type="secondary">{v || "—"}</Typography.Text>
      ),
    },
    {
      title: t("sample.type", "Tipo"),
      dataIndex: "sample_type",
      key: "sample_type",
      width: 90,
      render: (v: string) => (
        <Tag color={v === "leaf" ? "green" : "orange"}>
          {v === "leaf"
            ? t("sample.type_leaf", "Hoja")
            : t("sample.type_skin", "Piel")}
        </Tag>
      ),
    },
    {
      title: t("sample.origin", "Origen"),
      key: "origin",
      render: (_: unknown, r: SampleRow) => (
        <Space size={4} wrap>
          <span>
            {r.audit_plot_name || r.control_point_review_name || "—"}
          </span>
          {r.audit_plot_planting_label ? (
            <Tag color="geekblue" style={{ marginRight: 0 }}>
              {r.audit_plot_planting_label}
            </Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: t("sample.producer", "Productor"),
      dataIndex: "producer_name",
      key: "producer_name",
      ellipsis: true,
      render: (v: string | null) => v ?? "—",
    },
    {
      title: t("sample.farm", "Finca"),
      dataIndex: "farm_name",
      key: "farm_name",
      ellipsis: true,
      render: (v: string | null) => v ?? "—",
    },
    {
      title: t("sample.audit_date", "Fecha auditoría"),
      dataIndex: "audit_start_date",
      key: "audit_start_date",
      width: 130,
      render: (v: string | null) => (v ? dayjs(v).format("DD/MM/YYYY") : "—"),
    },
    {
      title: t("sample.audit", "Auditoría"),
      dataIndex: "audit_reference",
      key: "audit_reference",
      width: 150,
      render: (v: string, r: SampleRow) => (
        <Link to={`/audits/${r.audit}`}>{v || `#${r.audit}`}</Link>
      ),
    },
  ];

  return (
    <Card>
      <PageHeader
        icon={<ExperimentOutlined />}
        accent="#EB2F96"
        title={t(
          "samples.pending.title",
          "Muestras pendientes de código de laboratorio",
        )}
        subtitle={t(
          "samples.pending.subtitle",
          "Asigna el código real del laboratorio (5 caracteres) a las muestras sincronizadas desde la app.",
        )}
      />

      <Space size="middle" style={{ marginBottom: 16, width: "100%" }} wrap>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t(
            "samples.pending.search_placeholder",
            "Buscar por productor, finca, auditoría, parcela o referencia…",
          )}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 380 }}
        />
        <Select
          value={scope}
          onChange={(v) => setScope(v)}
          options={FILTER_OPTIONS}
          style={{ width: 220 }}
        />
      </Space>

      <Table<SampleRow>
        rowKey="id"
        loading={loading}
        dataSource={filtered}
        columns={columns}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        locale={{
          emptyText: (
            <Empty
              description={t(
                "samples.pending.empty",
                "No hay muestras pendientes de asignar código.",
              )}
            />
          ),
        }}
      />
    </Card>
  );
};

export default AuditorSamplesPending;
